import { Badge, Search } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { BodyText } from "@/components/ui/Text";
import { useSearchAllergies } from "@/features/Patient/MedicalRecords/MedicationsAllergies/network/useAllergies";
import { SearchIcon } from "lucide-react";
import { useState } from "react";
import { useDebounce } from "react-use";
import type { CodifiedAllergen } from "./useAmazonVisitIntake";

export const CiqAllergySearch = ({
  defaultSearchTerm,
  onAdd,
  isPending,
  addedAllergen,
  pendingAllergenName,
  addedLabel = "Added",
}: {
  defaultSearchTerm: string;
  onAdd: (allergen: CodifiedAllergen) => void;
  isPending: boolean;
  addedAllergen?: CodifiedAllergen;
  pendingAllergenName?: string;
  addedLabel?: string;
}) => {
  const [searchTerm, setSearchTerm] = useState(defaultSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] =
    useState(defaultSearchTerm);
  useDebounce(() => setDebouncedSearchTerm(searchTerm), 200, [searchTerm]);
  const { isLoading: isSearchLoading, data: searchResults } =
    useSearchAllergies(debouncedSearchTerm);

  const searchOptions =
    searchResults?.map((r) => ({
      label: r.allergyName,
      value: r.allergyCode,
      metadata: { system: r.allergyCodeSystem },
    })) ?? [];

  if (isPending) {
    return (
      <div className="mt-1 flex items-center justify-between gap-2 w-full px-2 min-h-[34px] border border-transparent rounded-lg">
        <BodyText size="sm">{pendingAllergenName}</BodyText>
        <Button
          size="xs"
          type="button"
          isLoading={true}
          className="shrink-0 transition-none bg-button-superclinical text-button-primary border border-ai"
        >
          Add
        </Button>
      </div>
    );
  }

  if (addedAllergen) {
    return (
      <div className="mt-1 flex items-center justify-between gap-2 w-full px-2 min-h-[34px] border border-transparent rounded-lg">
        <BodyText size="sm">{addedAllergen.name ?? ""}</BodyText>
        <div className="flex items-center shrink-0" style={{ height: 25 }}>
          <Badge variant="ai">
            <BodyText size="xs">{addedLabel}</BodyText>
          </Badge>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-1">
      <Search
        input={searchTerm}
        leftIcon={SearchIcon}
        isLoading={isSearchLoading}
        showLoadingSpinner
        options={searchOptions}
        placeholder="Search to add..."
        onInputChange={(val) => setSearchTerm(val)}
        onItemSelected={(item) =>
          onAdd({
            allergenId: item.value?.toString() ?? "",
            name: item.label ?? "",
            system: (item.metadata as { system?: string })?.system ?? "",
          })
        }
      />
    </div>
  );
};
